import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import "./Projects.css";
import { createPortal } from "react-dom";

/* ── Data ──────────────────────────────────────────────────────
   Featured carousel + spotlight grid use the Interiors photo set.
   The "View All" gallery has three tracks — Structural, Interiors,
   Projects — each mixing photos and video clips.

   NOTE: every label/tag shown on an image (title + category) has
   been standardized to a single consistent brand label per the
   requirement to replace old/inconsistent tag names throughout the
   gallery. The underlying image files, ids, and folder structure
   are untouched.
──────────────────────────────────────────────────────────────── */

const BRAND_LABEL = "Sarrathi Constructions";

const projects = Array.from({ length: 31 }, (_, index) => ({
  id: index + 1,
  image: `/Projectimg/Interiors/iimg${index + 1}.jpeg`,
  title: BRAND_LABEL,
  category: BRAND_LABEL,
}));

const buildImages = (count, folder, prefix, tabType) =>
  Array.from({ length: count }, (_, index) => ({
    id: `${prefix}-img-${index + 1}`,
    media: "image",
    src: `/Projectimg/${folder}/${prefix}${index + 1}.jpeg`,
    title: BRAND_LABEL,
    category: BRAND_LABEL,
    type: tabType,
  }));

const buildVideos = (count, folder, prefix, tabType) =>
  Array.from({ length: count }, (_, index) => ({
    id: `${prefix}-vid-${index + 1}`,
    media: "video",
    src: `/Projectimg/${folder}/${prefix}${index + 1}.mp4`,
    title: BRAND_LABEL,
    category: BRAND_LABEL,
    type: tabType,
  }));

const structuralProjects = [
  ...buildImages(21, "Structural", "simg", "structural"),
  ...buildVideos(6, "Structural", "svid", "structural"),
];

const interiorProjects = [
  ...buildImages(31, "Interiors", "iimg", "interiors"),
  ...buildVideos(2, "Interiors", "ivid", "interiors"),
];

const completedProjects = [
  ...buildImages(58, "Completedproj", "cimg", "completed"),
  ...buildVideos(8, "Completedproj", "cvid", "completed"),
];

const tabConfig = [
  { key: "structural", label: "Structural Works", short: "Structural", list: structuralProjects, dotClass: "project-tab-dot--structural" },
  { key: "interiors", label: "Interior Works", short: "Interiors", list: interiorProjects, dotClass: "project-tab-dot--interiors" },
  { key: "completed", label: "Projects", short: "Projects", list: completedProjects, dotClass: "project-tab-dot--done" },
];

// How many tiles render per "page". Lowered from 20 → 10: fewer
// images/videos are requested and decoded on first paint (and on
// every "Load More" click), which is what actually shortens the
// time-to-first-usable-gallery on slower connections/devices.
// Keeping the on-screen/DOM count bounded like this is also what
// removes the need for most lazy-loading machinery — there's never
// more than a couple of pages' worth of tiles mounted at once.
const PAGE_SIZE = 10;

// Entrance stagger is capped so Load More never queues up a long
// tail of delays — only the first N newly-mounted tiles animate
// with an increasing delay, the rest all land together. Lowered in
// step with PAGE_SIZE so a full page's stagger finishes quickly.
const STAGGER_CAP = 9;

/* ── Image-protection helpers ─────────────────────────────────
   Applied to every image/video rendered within this section
   (gallery tiles, lightbox, featured/spotlight images, and the
   small fullscreen-icon UI buttons) — nothing outside this file
   is touched.

   This is deliberately "casual-user" friction, not real DRM:
   - Blocks the native right-click / long-press context menu so
     "Save Image As…" isn't one click away.
   - Blocks the native browser drag-out-to-desktop gesture.
   - Blocks text/image selection so a drag-select can't grab it.
   - Anyone can still open devtools, use the browser's network
     panel, take a screenshot, or use a screen-capture tool — no
     client-side technique can prevent that, and none of the
     mainstream frameworks pretend otherwise.

   Defined once at module scope (not inside the component) so the
   same function reference is reused on every render instead of a
   brand-new closure being created per tile per render — this keeps
   props referentially stable for the memoized tile components. ─── */
const preventContextMenu = (e) => e.preventDefault();
const preventDragStart = (e) => e.preventDefault();
const NO_COPY_CLASS = "no-copy-media";

/* ── Scroll-performance helpers ───────────────────────────────
   Applied as inline styles (not CSS-file classes, so this ships
   correctly even without touching Projects.css) to every grid
   tile:

   - `contain: "content"` tells the browser a tile's internals
     never affect layout/paint outside its own box, so scrolling
     never forces it to re-measure/repaint the other 9-19 tiles on
     the page just because one of them changed (video frame,
     image fade-in, etc).
   - `contentVisibility: "auto"` + `containIntrinsicSize` skips
     layout/paint work entirely for tiles currently scrolled out of
     view, while still reserving their box size (via the intrinsic
     size hint) so nothing jumps when they scroll into view. This
     is the single biggest lever for "fluid scrolling on lower-end
     devices" because it stops the browser doing any work at all
     for off-screen tiles instead of just doing it lazily.
   - Both are simply ignored by browsers that don't support them
     (no feature-detection needed, no bugs introduced). ─────────── */
const TILE_PERF_STYLE = {
  contain: "content",
  contentVisibility: "auto",
  containIntrinsicSize: "260px 250px",
};

/* ── Icons ─────────────────────────────────────────────────── */
function EnlargeIcon({ className = "enlarge-icon" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlayIcon({ className = "play-icon" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  );
}

/* Corner-bracket frame — the modal's signature hover mark, styled
   after architectural crop / survey marks. Pure transform+opacity
   transitions only, so it never costs a repaint during scroll. */
const CornerFrame = memo(function CornerFrame() {
  return (
    <span className="tile-corner-frame" aria-hidden="true">
      <span className="corner corner--tl" />
      <span className="corner corner--tr" />
      <span className="corner corner--bl" />
      <span className="corner corner--br" />
    </span>
  );
});

/* ── Watermark ─────────────────────────────────────────────────
   Purely a CSS layer (see `.wm-protect::after` in Projects.css) —
   a single shared, tiled SVG background-image, not a per-image DOM
   node. That matters for performance: with up to ~90 tiles in a
   tab, rendering the watermark as real DOM text (spans etc.) would
   add hundreds of extra nodes for the browser to paint/scroll.
   Instead every protected surface just gets one `wm-protect` class
   name — the browser composites the (cached, single-asset)
   background pattern the same cheap way it composites any other
   tiled background, and it survives image optimization/CDN
   transforms because it is never part of the raster photo at all,
   it's rendered fresh by the browser every time. ───────────────── */
const WM_CLASS = "wm-protect";

/* ── useInViewOnce ─────────────────────────────────────────────
   Lightweight IntersectionObserver hook: reports true the first
   time the attached element enters (or nears) the viewport, then
   disconnects. Used below to stop <video> elements from starting
   network requests the instant they're mounted.

   This matters because native `loading="lazy"` only defers
   <img>/<iframe> — it has no effect on <video>. A <video
   preload="metadata"> starts fetching the instant it's in the DOM,
   regardless of whether it's on screen. With up to PAGE_SIZE (10)
   grid tiles mounted per page — several of which can be videos —
   that means every "Load More"/tab-open used to fire off several
   simultaneous video metadata requests competing for the same
   connection pool as the visible photo tiles, which is what was
   actually slowing the *images* down. ─────────────────────────── */
function useInViewOnce(rootMargin = "300px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      // Environments without IO support: fail open rather than
      // silently never loading the thumbnail.
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView];
}

/* ── CrossfadeImage ──────────────────────────────────────────────
   Used by the featured carousel + spotlight grid only (both always
   above the fold). Renders TWO real <img> layers and crossfades
   between them on slide change.

   IMPORTANT: this used to pre-resolve each image with a background
   `new Image()` object inside a useEffect, and only rendered a real
   <img> once that finished loading. That meant nothing was ever
   requested until *after* mount → effect → JS `Image()` fetch →
   load event → setState → re-render — a full extra round trip
   before the browser even started downloading the photo. On a
   slower dev-server JS bundle that gap is very visible: the card's
   background color + the CSS watermark (which paints immediately,
   since it doesn't depend on the photo at all) show up alone for a
   noticeable moment before the image appears.

   Fix: render the real <img> tag immediately, in the initial
   render, with `loading="eager"` — the browser starts the request
   the instant this paints, same as any other <img>. Extension
   fallback (.jpeg → .jpg → .png) and the crossfade-on-slide-change
   behavior are both preserved, just driven by the real <img>'s own
   onLoad/onError events instead of a shadow copy. ────────────────── */

// Source files are served as .jpeg, not .jpg — try the real
// extension first so a correct guess costs one request, not two.
const IMG_EXTENSIONS = ["jpeg", "jpg", "png"];

const resolveCrossfadeSrc = (src, extIndex) => {
  const base = src.replace(/\.[^/.]+$/, "");
  return `${base}.${IMG_EXTENSIONS[extIndex]}`;
};

const CrossfadeImage = memo(function CrossfadeImage({
  src,
  alt,
  wrapClassName,
  layerClassName,
  fetchPriority = "auto",
  protect = true,
}) {
  // Two slots, each independently tracking { src, extIndex }. Slot 0
  // starts populated immediately (with the current `src`) so its
  // <img> is present on the very first render — that's what removes
  // the empty-box flash. Slot 1 only gets filled in when the `src`
  // prop changes (i.e. the carousel advances), so a second image is
  // never requested unless a crossfade is actually needed.
  const [slots, setSlots] = useState(() => [{ src, extIndex: 0 }, null]);
  const [activeSlot, setActiveSlot] = useState(0);
  const requestedSrc = useRef(src);

  useEffect(() => {
    if (requestedSrc.current === src) return;
    requestedSrc.current = src;
    setSlots((prev) => {
      const incomingSlot = activeSlot === 0 ? 1 : 0;
      const next = [...prev];
      next[incomingSlot] = { src, extIndex: 0 };
      return next;
    });
  }, [src, activeSlot]);

  // Only swap the visible layer once the incoming photo has actually
  // finished loading/decoding — this is what keeps the crossfade
  // (rather than a pop/flash) on every subsequent slide change.
  const handleLoad = useCallback((slotIndex) => {
    setActiveSlot(slotIndex);
  }, []);

  const handleError = useCallback((slotIndex) => {
    setSlots((prev) => {
      const layer = prev[slotIndex];
      if (!layer || layer.extIndex >= IMG_EXTENSIONS.length - 1) return prev;
      const next = [...prev];
      next[slotIndex] = { ...layer, extIndex: layer.extIndex + 1 };
      return next;
    });
  }, []);

  const finalWrapClassName = `${wrapClassName || ""} ${protect ? WM_CLASS : ""}`.trim();
  const finalLayerClassName = `crossfade-layer ${layerClassName || ""} ${
    protect ? NO_COPY_CLASS : ""
  }`.trim();

  return (
    <div className={finalWrapClassName}>
      {slots.map((slot, i) =>
        slot ? (
          <img
            key={`${i}-${slot.src}-${slot.extIndex}`}
            src={resolveCrossfadeSrc(slot.src, slot.extIndex)}
            alt={alt}
            className={finalLayerClassName}
            style={{ opacity: activeSlot === i ? 1 : 0 }}
            fetchPriority={i === 0 ? fetchPriority : "auto"}
            loading="eager"
            decoding="async"
            draggable={false}
            onLoad={() => handleLoad(i)}
            onError={() => handleError(i)}
            onContextMenu={protect ? preventContextMenu : undefined}
            onDragStart={protect ? preventDragStart : undefined}
          />
        ) : null
      )}
    </div>
  );
});

/* ── SmartImage ────────────────────────────────────────────────
   Image with extension fallback and a plain opacity fade once
   loaded. This is cheap now — with pagination there are only ever
   ~10-20 of these mounted at a time, never the full 60-90. Fixed
   `width`/`height` (set by the tiles below) reserve the box before
   the photo arrives, which is what prevents layout shift/flicker
   as images pop in. ─────────────────────────────────────────────── */
const SmartImage = memo(function SmartImage({
  src,
  alt,
  className,
  loading = "lazy",
  decoding = "async",
  width,
  height,
  fetchPriority = "auto",
  protect = true,
}) {
  const base = src.replace(/\.[^/.]+$/, "");
  const [extIndex, setExtIndex] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const currentSrc = `${base}.${IMG_EXTENSIONS[extIndex]}`;

  const handleError = () => {
    if (extIndex < IMG_EXTENSIONS.length - 1) {
      setExtIndex((i) => i + 1);
    } else {
      setFailed(true);
    }
  };

  if (failed) {
    return (
      <div className={`${className || ""} image-fallback`} role="img" aria-label={alt}>
        <EnlargeIcon />
      </div>
    );
  }

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={`${className || ""} ${loaded ? "is-loaded" : ""} ${
        protect ? NO_COPY_CLASS : ""
      }`}
      loading={loading}
      decoding={decoding}
      width={width}
      height={height}
      fetchPriority={fetchPriority}
      draggable={false}
      onContextMenu={protect ? preventContextMenu : undefined}
      onDragStart={protect ? preventDragStart : undefined}
      onError={handleError}
      onLoad={() => setLoaded(true)}
    />
  );
});

/* ── SmartVideo ────────────────────────────────────────────────
   Extension-fallback video element. Used by the lightbox player
   (with controls/autoplay) AND, in a stripped-down muted/no-controls
   form, as the grid's video-thumbnail preview (see VideoTile). ──── */
const VIDEO_EXTENSIONS = ["mp4"];

const SmartVideo = memo(function SmartVideo({
  src,
  className,
  controls = false,
  autoPlay = false,
  preload = "auto",
  muted = false,
  loop = false,
  protect = true,
}) {
  const base = src.replace(/\.[^/.]+$/, "");
  const [extIndex, setExtIndex] = useState(0);
  const [failed, setFailed] = useState(false);

  const currentSrc = `${base}.${VIDEO_EXTENSIONS[extIndex]}`;

  const handleError = () => {
    if (extIndex < VIDEO_EXTENSIONS.length - 1) {
      setExtIndex((i) => i + 1);
    } else {
      setFailed(true);
    }
  };

  if (failed) {
    return (
      <div className={`${className || ""} image-fallback`} role="img" aria-label="Video unavailable">
        <PlayIcon />
      </div>
    );
  }

  return (
    <video
      key={currentSrc}
      src={currentSrc}
      className={`${className || ""} ${protect ? NO_COPY_CLASS : ""}`}
      controls={controls}
      controlsList={protect ? "nodownload noremoteplayback" : undefined}
      disablePictureInPicture={protect}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline
      preload={preload}
      onContextMenu={protect ? preventContextMenu : undefined}
      onDragStart={protect ? preventDragStart : undefined}
      onError={handleError}
    />
  );
});

/* ── Grid tiles ────────────────────────────────────────────────
   ImageTile renders a real (lazy, native-loading) image.
   VideoTile shows a real muted, metadata-preloaded <video> as its
   thumbnail (browsers paint the first frame automatically once
   metadata/poster-less video loads), with the play badge layered
   on top — so the tile actually previews the clip instead of just
   showing a flat color card. The clip only starts *playing* once
   the user opens it in the lightbox. The <video> itself is now
   gated behind useInViewOnce (see above) so it doesn't start
   fetching until it's actually about to be scrolled into view.

   Each tile carries a `--i` custom property used purely for the
   entrance stagger in CSS — capped so Load More never produces a
   long queue of delays — plus the shared TILE_PERF_STYLE (contain +
   content-visibility) so scrolling stays smooth even with several
   pages' worth of tiles mounted.

   Both tile components are memoized so that growing `visibleList`
   via "Load More" (or the parent re-rendering for unrelated state)
   doesn't force React to re-diff and repaint tiles that are
   already on screen and unchanged. ─────────────────────────────── */
const TileOverlay = memo(function TileOverlay({ index, title, actionLabel }) {
  return (
    <div className="all-project-overlay">
      <span className="all-project-id">{String(index + 1).padStart(2, "0")}</span>
      <div className="all-project-meta">
        <p className="all-project-title">{title}</p>
        <span className="all-project-view">
          <img
            src="/FullScreen/Bigscreen.png"
            alt=""
            className="gallery-fullscreen-icon"
            draggable={false}
            onContextMenu={preventContextMenu}
            onDragStart={preventDragStart}
          />
          {actionLabel}
        </span>
      </div>
    </div>
  );
});

const ImageTile = memo(function ImageTile({ project, index, onOpen }) {
  // Only the first row (5-col grid) is treated as truly above-the-
  // fold and gets eager/high-priority loading. Previously this was
  // index < 10 (two rows) — but with the initial page now only 10
  // tiles total, eager-loading the whole page would have undone the
  // point of shrinking PAGE_SIZE. Everything from the second row on
  // uses native lazy-loading, so the browser only fetches what's
  // actually about to be seen.
  const isAboveFold = index < 5;

  const handleClick = useCallback(() => onOpen(index), [onOpen, index]);

  return (
    <button
      type="button"
      className={`all-project-item ${WM_CLASS}`}
      style={{ "--i": Math.min(index, STAGGER_CAP), ...TILE_PERF_STYLE }}
      onClick={handleClick}
    >
      <SmartImage
        src={project.src}
        alt={project.title}
        className="all-project-img"
        width={300}
        height={250}
        loading={isAboveFold ? "eager" : "lazy"}
        fetchPriority={isAboveFold ? "high" : "auto"}
      />
      <CornerFrame />
      <span className="tile-badge" aria-hidden="true">
        <EnlargeIcon />
      </span>
      <TileOverlay index={index} title={project.title} actionLabel="View" />
    </button>
  );
});

const VideoTile = memo(function VideoTile({ project, index, onOpen }) {
  const [thumbRef, inView] = useInViewOnce();
  const handleClick = useCallback(() => onOpen(index), [onOpen, index]);

  return (
    <button
      type="button"
      className="all-project-item all-project-item--video"
      style={{ "--i": Math.min(index, STAGGER_CAP), ...TILE_PERF_STYLE }}
      onClick={handleClick}
    >
      <div className={`all-project-video-card ${WM_CLASS}`} ref={thumbRef}>
        {inView ? (
          <SmartVideo
            src={project.src}
            className="all-project-video-thumb"
            preload="metadata"
            muted
          />
        ) : (
          // Same box, same background gradient from the parent —
          // visually identical to the pre-load state a real video
          // would show anyway, but costs zero network requests
          // until the tile is actually about to be seen.
          <div className="all-project-video-thumb" aria-hidden="true" />
        )}
        <span className="all-project-video-play">
          <PlayIcon className="play-icon play-icon--lg" />
        </span>
      </div>
      <CornerFrame />
      <span className="tile-badge" aria-hidden="true">
        <PlayIcon />
      </span>
      <TileOverlay index={index} title={project.title} actionLabel="Play" />
    </button>
  );
});

const GridTile = memo(function GridTile({ project, index, onOpen }) {
  return project.media === "video" ? (
    <VideoTile project={project} index={index} onOpen={onOpen} />
  ) : (
    <ImageTile project={project} index={index} onOpen={onOpen} />
  );
});

/* ── useScrollLock ─────────────────────────────────────────────
   Real modal scroll-lock. Setting `body { overflow: hidden }` on
   its own does NOT stop touch scrolling on iOS Safari — once you
   hit the top/bottom of the modal's internal scroller, the scroll
   "chains" onto the page behind it. The fix is to actually freeze
   the body in place: remember the current scroll offset, pin the
   body with `position: fixed` at that offset, and only undo it
   (restoring the exact scroll position) once every overlay is
   closed. ─────────────────────────────────────────────────────── */
function useScrollLock(isLocked) {
  const scrollYRef = useRef(0);

  useEffect(() => {
    if (!isLocked) return;

    scrollYRef.current = window.scrollY;

    const { style } = document.body;
    const prev = {
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      width: style.width,
      overflow: style.overflow,
    };

    style.position = "fixed";
    style.top = `-${scrollYRef.current}px`;
    style.left = "0";
    style.right = "0";
    style.width = "100%";
    style.overflow = "hidden";

    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.left = prev.left;
      style.right = prev.right;
      style.width = prev.width;
      style.overflow = prev.overflow;
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isLocked]);
}

/* ── useHeroPreload ────────────────────────────────────────────
   Injects a <link rel="preload" as="image"> for the very first
   hero photo directly into <head> on mount, before React even
   commits the <img>. This is the single highest-leverage LCP win
   available here: it lets the browser's preload scanner start
   fetching the hero image while it's still parsing/streaming the
   rest of the HTML, instead of waiting for React to render, run
   effects, and only then have the <img> discovered. The link is
   removed on unmount so it never lingers if this section is
   conditionally mounted elsewhere in the app. ───────────────────── */
function useHeroPreload(href) {
  useEffect(() => {
    if (!href || typeof document === "undefined") return;
    const existing = document.querySelector(`link[data-hero-preload="${href}"]`);
    if (existing) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    link.fetchPriority = "high";
    link.dataset.heroPreload = href;
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, [href]);
}

function Projects() {
  const [currentProject, setCurrentProject] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("structural");
  const [lightboxSource, setLightboxSource] = useState("featured"); // "featured" | "gallery"
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Kick off the LCP image fetch as early as possible.
  useHeroPreload(projects[0]?.image);

  const showNextProject = useCallback(() => {
    setCurrentProject((prev) => (prev === projects.length - 1 ? 0 : prev + 1));
  }, []);

  const showPreviousProject = useCallback(() => {
    setCurrentProject((prev) => (prev === 0 ? projects.length - 1 : prev - 1));
  }, []);

  const openLightbox = useCallback((index) => {
    setLightboxSource("featured");
    setCurrentProject(index);
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
  const openGallery = useCallback(() => setIsGalleryOpen(true), []);
  const closeGallery = useCallback(() => setIsGalleryOpen(false), []);

  const activeTabIndex = tabConfig.findIndex((tab) => tab.key === activeTab);
  const activeList = tabConfig[activeTabIndex].list;

  const visibleList = useMemo(
    () => activeList.slice(0, visibleCount),
    [activeList, visibleCount]
  );
  const hasMore = visibleCount < activeList.length;
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Cumulative slice off the same underlying `activeList` array —
  // each "Load More" just raises `visibleCount` by PAGE_SIZE (now
  // 10), so every image that was already visible stays at the same
  // index and no image is ever skipped or duplicated, right up to
  // the final (possibly partial) page.
  const loadMore = useCallback(
    () => setVisibleCount((c) => Math.min(c + PAGE_SIZE, activeList.length)),
    [activeList.length]
  );

  // useCallback so this stays referentially stable across renders —
  // paired with React.memo on GridTile/ImageTile/VideoTile above,
  // this stops "Load More" from causing the already-rendered tiles
  // to re-render just because the callback prop got a new identity
  // every time.
  const openProjectFromGallery = useCallback(
    (visibleIndex) => {
      const project = visibleList[visibleIndex];
      if (!project) return;
      const trueIndex = activeList.findIndex((p) => p.id === project.id);
      setLightboxSource("gallery");
      setGalleryIndex(trueIndex === -1 ? 0 : trueIndex);
      setIsLightboxOpen(true);
      // gallery stays open behind the lightbox
    },
    [visibleList, activeList]
  );

  const showNextGalleryItem = useCallback(() => {
    setGalleryIndex((prev) => (prev === activeList.length - 1 ? 0 : prev + 1));
  }, [activeList.length]);

  const showPreviousGalleryItem = useCallback(() => {
    setGalleryIndex((prev) => (prev === 0 ? activeList.length - 1 : prev - 1));
  }, [activeList.length]);

  const lightboxItem =
    lightboxSource === "gallery" ? activeList[galleryIndex] : projects[currentProject];
  const lightboxCount = lightboxSource === "gallery" ? activeList.length : projects.length;
  const lightboxPosition = lightboxSource === "gallery" ? galleryIndex : currentProject;
  const goNext = lightboxSource === "gallery" ? showNextGalleryItem : showNextProject;
  const goPrev = lightboxSource === "gallery" ? showPreviousGalleryItem : showPreviousProject;
  const lightboxIsVideo = lightboxSource === "gallery" && lightboxItem?.media === "video";

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (isLightboxOpen) {
          closeLightbox();
        } else if (isGalleryOpen) {
          closeGallery();
        }
      }
      if (isLightboxOpen && e.key === "ArrowRight") goNext();
      if (isLightboxOpen && e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, isGalleryOpen, goNext, goPrev, closeLightbox, closeGallery]);

  // Real scroll-lock (see useScrollLock above) instead of a bare
  // `body.style.overflow = "hidden"` toggle.
  useScrollLock(isLightboxOpen || isGalleryOpen);

  useEffect(() => {
    // Passive/no-op work only while neither overlay is open — and
    // the interval itself is cleared on unmount, so leaving this
    // page never leaves a ticking timer or detached closures behind.
    const interval = setInterval(() => {
      if (!isLightboxOpen && !isGalleryOpen) showNextProject();
    }, 5000);
    return () => clearInterval(interval);
  }, [isLightboxOpen, isGalleryOpen, showNextProject]);

  const previewProjects = useMemo(() => {
    const slice = projects.slice(currentProject, currentProject + 6);
    return slice.length === 6 ? slice : [...slice, ...projects.slice(0, 6 - slice.length)];
  }, [currentProject]);

  const handleTabChange = useCallback(
    (tab) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
      setGalleryIndex(0);
      setVisibleCount(PAGE_SIZE);
    },
    [activeTab]
  );

  return (
    <section className="projects-page" id="projects">
      <div className="projects-heading">
        <p className="section-eyebrow">Our Projects</p>
        <h1>Spaces built with quality, precision, and purpose.</h1>
        <p>
          Explore some of the construction projects completed by Sarrathi
          Constructions.
        </p>
      </div>

      {/* ── FEATURED PROJECT — full-bleed + floating glass card ── */}
      <div className="featured-project">
        <CrossfadeImage
          src={projects[currentProject].image}
          alt={projects[currentProject].title}
          wrapClassName="featured-bg-image"
          fetchPriority="high"
        />

        <div className="featured-overlay" />

        <button
          type="button"
          className="view-project-button"
          onClick={() => openLightbox(currentProject)}
          aria-label="View Project Fullscreen"
        >
          <img
            src="/FullScreen/Bigscreen.png"
            alt="View Fullscreen"
            className="fullscreen-icon"
            draggable={false}
            onContextMenu={preventContextMenu}
            onDragStart={preventDragStart}
          />
        </button>

        <button
          type="button"
          className="project-arrow project-arrow-left"
          onClick={showPreviousProject}
          aria-label="Previous project"
        >
          ←
        </button>
        <button
          type="button"
          className="project-arrow project-arrow-right"
          onClick={showNextProject}
          aria-label="Next project"
        >
          →
        </button>

        <div className="featured-glass-card">
          <h2 className="glass-card-title">Sarrathi Constructions Projects</h2>

          <div className="glass-card-divider" />

          <div className="glass-card-stats">
            <div className="glass-stat">
              <strong>30+</strong>
              <span>Projects</span>
            </div>
            <div className="glass-stat-sep" />
            <div className="glass-stat">
              <strong>10+</strong>
              <span>Years</span>
            </div>
            <div className="glass-stat-sep" />
            <div className="glass-stat">
              <strong>100%</strong>
              <span>Quality</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── PROJECT GALLERY SPOTLIGHT GRID ── */}
      <div className="gallery-section">
        <div className="gallery-section-header">
          <div className="gallery-section-left">
            <p className="section-eyebrow">Project Gallery</p>
            <h2 className="gallery-section-title">Our Completed Works</h2>
          </div>
          <button type="button" className="view-all-projects-button" onClick={openGallery}>
            View All Projects →
          </button>
        </div>

        <div className="spotlight-grid">
          {previewProjects[0] &&
            (() => {
              const idx = projects.findIndex((p) => p.id === previewProjects[0].id);
              return (
                <button
                  type="button"
                  className="spotlight-card spotlight-card--large"
                  onClick={() => openLightbox(idx)}
                  key="spotlight-large"
                >
                  <CrossfadeImage
                    src={previewProjects[0].image}
                    alt={previewProjects[0].title}
                    wrapClassName="spotlight-media"
                    fetchPriority="high"
                  />
                  <div className="spotlight-hover-panel">
                    <span className="spotlight-enlarge-badge">
                      <EnlargeIcon />
                    </span>
                    <div className="spotlight-hover-content">
                      <h3 className="spotlight-title">{previewProjects[0].title}</h3>
                      <span className="spotlight-view-btn">View Project →</span>
                    </div>
                  </div>
                </button>
              );
            })()}

          <div className="spotlight-small-grid">
            {previewProjects.slice(1).map((project, i) => {
              const idx = projects.findIndex((p) => p.id === project.id);
              return (
                <button
                  type="button"
                  className="spotlight-card spotlight-card--small"
                  key={`spotlight-small-${i}`}
                  onClick={() => openLightbox(idx)}
                >
                  <CrossfadeImage
                    src={project.image}
                    alt={project.title}
                    wrapClassName="spotlight-media"
                  />
                  <div className="spotlight-hover-panel">
                    <span className="spotlight-enlarge-badge">
                      <EnlargeIcon />
                    </span>
                    <div className="spotlight-hover-content">
                      <h3 className="spotlight-title">{project.title}</h3>
                      <span className="spotlight-view-btn">View →</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── ALL PROJECTS MODAL ──────────────────────────────────
          Rebuilt around three fixes for the tearing/lag you were
          seeing, plus a new "site drawings" visual language:

          1. `.all-projects-panel` gets `will-change: transform` so
             the browser composites its (large, blurred) shadow as
             a cached layer instead of repainting a 40px/100px blur
             on every scroll frame — that repaint was the single
             biggest cause of the tearing.
          2. The tab-dot "live" pulse no longer animates box-shadow
             (expensive, main-thread, runs the whole time the modal
             is open) — it's a transform+opacity ring now, which is
             compositor-only and effectively free.
          3. Every tile gets `contain: content` (+ `content-visibility:
             auto`, see TILE_PERF_STYLE above), so scrolling never
             forces the browser to re-measure/repaint/paint tiles
             that are off-screen or unchanged.

          Still just one overlay element that is both the fixed
          full-viewport backdrop AND the scroll container — no
          nested fixed/absolute split fighting itself. Pagination
          (PAGE_SIZE tiles per "page", now 10) keeps DOM size bounded
          and shortens the initial decode/paint work even further. ── */}
{isGalleryOpen && createPortal(
  <div className="all-projects-overlay" onClick={closeGallery}>
    <div className="all-projects-panel" onClick={(e) => e.stopPropagation()}>
      <div className="all-projects-header">
        <div>
          <p className="section-eyebrow">Project Gallery</p>
          <h2>Explore By Category</h2>
          <p className="all-projects-subtext">
            From structural groundwork to interior finishing to final
            handover — browse photos and walkthrough videos from every
            stage of our builds.
          </p>
        </div>
        <button
          type="button"
          className="modal-close-button"
          onClick={closeGallery}
          aria-label="Close project gallery"
        >
          ×
        </button>
      </div>

      <div className="all-projects-controls">
        <div className="project-tabs" role="tablist" aria-label="Project category">
          <div
            className={`project-tabs-thumb ${
              activeTabIndex === 1
                ? "project-tabs-thumb--mid"
                : activeTabIndex === 2
                ? "project-tabs-thumb--right"
                : ""
            }`}
            aria-hidden="true"
          />
          {tabConfig.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`project-tab ${activeTab === tab.key ? "project-tab--active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <span className={`project-tab-dot ${tab.dotClass}`} />
              <span className="project-tab-label">
                <span className="project-tab-label-full">{tab.label}</span>
                <span className="project-tab-label-short">{tab.short}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="all-projects-grid" key={activeTab}>
        {visibleList.map((project, index) => (
          <GridTile
            key={project.id}
            project={project}
            index={index}
            onOpen={openProjectFromGallery}
          />
        ))}
      </div>

      {hasMore && (
        <div className="all-projects-load-more">
          <button type="button" className="load-more-button" onClick={loadMore}>
            Load More ...
          </button>
        </div>
      )}
    </div>
  </div>,
  document.body
)}

      {/* ── LIGHTBOX ── */}
{isLightboxOpen && lightboxItem && createPortal(
  <div className="project-lightbox" onClick={closeLightbox}>
    <div className="project-lightbox-content" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="lightbox-close"
        onClick={closeLightbox}
        aria-label="Close project preview"
      >
        ×
      </button>

      <button
        type="button"
        className="lightbox-arrow lightbox-arrow-left"
        onClick={goPrev}
        aria-label="Previous project"
      >
        ←
      </button>

      <div className={`lightbox-media ${WM_CLASS}`}>
        {lightboxIsVideo ? (
          <SmartVideo
            src={lightboxItem.src}
            className="lightbox-video-player"
            controls
            autoPlay
            preload="auto"
          />
        ) : (
          <SmartImage
            src={lightboxSource === "gallery" ? lightboxItem.src : lightboxItem.image}
            alt={lightboxItem.title}
            loading="eager"
            fetchPriority="high"
          />
        )}
      </div>

      <button
        type="button"
        className="lightbox-arrow lightbox-arrow-right"
        onClick={goNext}
        aria-label="Next project"
      >
        →
      </button>

      <div className="lightbox-project-info">
        <div>
          <h2>{lightboxItem.title}</h2>
        </div>
        <span>
          {lightboxPosition + 1} / {lightboxCount}
        </span>
      </div>
    </div>
  </div>,
  document.body
)}
    </section>
  );
}

export default Projects;